import React from "react";
import { Alert, Box, Stack } from "@mui/material";
import LoadingState from "../../components/common/LoadingState";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  fetchConnectionContext,
  fetchSprintPartnerProfile,
  getSprintWorkspacePath,
  startSprint,
} from "../../api/trustbuds";
import { ACTION_MESSAGE_PARTNER, resolveWebAction } from "../../navigation/actionIntents";
import PersonActivityList from "./components/PersonActivityList";
import PersonFocusCard from "./components/PersonFocusCard";
import PersonHeader from "./components/PersonHeader";
import PersonPrimaryActions from "./components/PersonPrimaryActions";
import PersonRelationshipCard from "./components/PersonRelationshipCard";

function mergeSprintPartnerContext(base, sprintProfile) {
  if (!sprintProfile?.sprint_id) return base;

  const sprintGoal = sprintProfile.sprint_goal || null;
  const sprintActions = Array.isArray(sprintProfile.sprint_actions)
    ? sprintProfile.sprint_actions
    : [];
  const actionTitles = sprintActions
    .map((item) => String(item?.title || "").trim())
    .filter(Boolean)
    .slice(0, 2);
  const actionSummary = actionTitles.join(" · ");
  const nextGoal =
    sprintGoal?.title || sprintGoal?.summary || actionSummary
      ? {
          title: sprintGoal?.title || base?.shared_goal_summary?.title || null,
          summary:
            sprintGoal?.summary ||
            actionSummary ||
            base?.shared_goal_summary?.summary ||
            null,
        }
      : base?.shared_goal_summary || null;

  return {
    ...base,
    shared_goal_summary: nextGoal,
    shared_focus_statement:
      actionSummary || sprintGoal?.summary || base?.shared_focus_statement || null,
    sprint_id: sprintProfile.sprint_id,
    relationship_state: {
      ...(base?.relationship_state || {}),
      kind: "active_sprint",
      sprint_id: sprintProfile.sprint_id,
      message:
        base?.relationship_state?.message || "You're currently in Accountability together.",
    },
    visibility_flags: {
      ...(base?.visibility_flags || {}),
      sprint_goal: Boolean(sprintProfile?.visibility_flags?.goal),
      sprint_actions: Boolean(sprintProfile?.visibility_flags?.actions),
      sprint_progress: Boolean(sprintProfile?.visibility_flags?.progress),
    },
  };
}

function getMessagePath(person) {
  return resolveWebAction(ACTION_MESSAGE_PARTNER, {
    partnerName: person?.name,
    partnerUserId: person?.user_id,
    sprintId: person?.sprint_id,
    connectionId: person?.connection_id,
    source: "person_context",
  }).route;
}

export default function PersonContextPage() {
  const navigate = useNavigate();
  const { connectionId } = useParams();
  const [searchParams] = useSearchParams();
  const sprintId = searchParams.get("sprintId");
  const source = searchParams.get("source");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [primaryBusy, setPrimaryBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!connectionId) {
      setError("Connection not found.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const base = await fetchConnectionContext(connectionId);
      let payload = base;
      if (sprintId) {
        try {
          const sprintProfile = await fetchSprintPartnerProfile(sprintId);
          payload = mergeSprintPartnerContext(base, sprintProfile);
        } catch {
          payload = base;
        }
      }
      setData(payload || null);
    } catch (err) {
      setError(err?.message || "Unable to load this person right now.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [connectionId, sprintId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handlePrimaryAction = React.useCallback(async () => {
    if (!data?.connection_id) return;
    if (data?.sprint_id) {
      navigate(
        getSprintWorkspacePath({
          sprintId: data.sprint_id,
          connectionId: data.connection_id,
          userId: data.user_id,
        })
      );
      return;
    }

    setPrimaryBusy(true);
    try {
      const result = await startSprint(data.connection_id, {
        userId: data.user_id,
        focus: "chat",
        source: "person_context",
      });
      window.dispatchEvent(new Event("trustbud:connections-changed"));
      if (result?.code === "already_active") {
        window.alert(`You're already in Accountability with ${data.name}.`);
      }
      if (result?.code === "request_sent" || result?.code === "request_pending") {
        window.alert(`${data.name} can accept your request to start Accountability.`);
        return;
      }
      if (result?.route) navigate(result.route);
    } catch (err) {
      const activeSprintId = err?.errors?.active_sprint_id || err?.errors?.sprint_id || null;
      if (activeSprintId) {
        navigate(
          getSprintWorkspacePath({
            sprintId: activeSprintId,
            connectionId: data.connection_id,
            userId: data.user_id,
            focus: "chat",
          })
        );
        return;
      }
      setError(err?.message || "Unable to start Accountability right now.");
    } finally {
      setPrimaryBusy(false);
    }
  }, [data, navigate]);

  const handleSecondaryAction = React.useCallback(() => {
    if (!data) return;
    navigate(getMessagePath(data) || "/activity?tab=messages");
  }, [data, navigate]);

  const handleBack = React.useCallback(() => {
    if (source === "sprint") {
      navigate("/sprint");
      return;
    }
    if (source === "connections") {
      navigate("/connections");
      return;
    }
    navigate("/activity?tab=messages");
  }, [navigate, source]);

  const backLabel =
    source === "sprint"
      ? "Back to Accountability"
      : source === "connections"
        ? "Back to Connections"
        : "Back to Activity";

  return (
    <Box sx={{ maxWidth: 980 }}>
      {loading ? (
        <LoadingState
          title="Building this profile"
          body="Bringing relationship context, shared focus, and recent activity into place."
          rows={4}
          sx={{ minHeight: 360 }}
        />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : data ? (
        <Stack spacing={2}>
          <PersonHeader
            person={data}
            onBack={handleBack}
            backLabel={backLabel}
          />
          <PersonPrimaryActions
            sprintActive={Boolean(data?.sprint_id)}
            partnerName={data?.name}
            primaryBusy={primaryBusy}
            onMessageAction={handleSecondaryAction}
            onSprintAction={handlePrimaryAction}
          />
          <PersonFocusCard person={data} />
          {(data?.recent_activity || []).length ? <PersonActivityList items={data.recent_activity} /> : null}
          <PersonRelationshipCard
            relationshipState={data.relationship_state}
            lightContext={data.light_context}
          />
        </Stack>
      ) : (
        <Alert severity="info">This person is not available right now.</Alert>
      )}
    </Box>
  );
}
